import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CalendarDays, ChevronDown, FileText, School, Stethoscope, User, UserRoundCheck } from 'lucide-react';
import { getUrl } from 'aws-amplify/storage';
import { useUpdateAdmissionsChildApplicationMutation } from '../../../../state/domainRouterApi';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import { useAdmissionsApplication, useAdmissionsDocuments, useAdmissionsTimeline } from '../../adapters/liveData';
import { formatEventLabel } from '../../utils/formatEventLabel';
import { buildAdmissionsChildMedicalSummary } from '../../utils/profileDisplayValues';

type DetailContext = {
  applicationId: string;
  isEditing?: boolean;
  editResetToken?: number;
  detailStatus?: string;
  assigneeName?: string;
  staffMembers?: Array<{ id?: string; name?: string }>;
};

type ChildProfile = {
  id: string;
  name: string;
  preferredName: string;
  hebrewName: string;
  applicationYear: string;
  gradeApplyingFor: string;
  dateOfBirth: string;
  placeOfBirth: string;
  languageAtHome: string;
  currentSchool: string;
  currentGrade: string;
  principalName: string;
  schoolPhone: string;
  pediatrician: string;
  pediatricianPhone: string;
  dentist: string;
  dentistPhone: string;
  conditions: string;
  division: string;
  status: string;
  assigneeName: string;
  avatarUrl: string;
};

type TimelineCard = {
  id: string;
  title: string;
  dateLabel: string;
  dayLabel: string;
  timeLabel: string;
  monthLabel: string;
};

type StaffOption = {
  id: string;
  name: string;
  persistId: string | null;
};

const HEBREW_KEYS = [
  'ק', 'ר', 'א', 'ט', 'ו', 'ן', 'ם', 'פ',
  'ש', 'ד', 'ג', 'כ', 'ע', 'י', 'ח', 'ל', 'ך', 'ף',
  'ז', 'ס', 'ב', 'נ', 'מ', 'צ', 'ת', 'ץ',
];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asRecordArray = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value)
    ? value
        .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
        .map((entry) => entry as Record<string, unknown>)
    : [];

const readString = (value: unknown): string => String(value ?? '').trim();
const UUID_LIKE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STORAGE_PATH_PATTERN = /^(uploads|admissions)\//i;

const readUnixSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isPlaceholder = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return normalized.length === 0 || normalized === 'not_published' || normalized === 'none' || normalized === 'n/a';
};

const toDisplayOrFallback = (value: unknown, fallback = 'Not specified'): string => {
  const raw = readString(value);
  return isPlaceholder(raw) ? fallback : raw;
};

const sanitizeAssigneeLabel = (value: unknown): string => {
  const raw = readString(value);
  if (isPlaceholder(raw)) return '';
  if (UUID_LIKE_PATTERN.test(raw)) return '';
  return raw;
};

const isStoragePath = (value: string): boolean => STORAGE_PATH_PATTERN.test(value);

const titleCaseWords = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');

const normalizeDelimitedValue = (value: unknown): string =>
  readString(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeGradeLabel = (value: unknown): string => {
  const normalized = normalizeDelimitedValue(value);
  if (!normalized) return 'Not specified';

  const withoutGrade = normalized.replace(/^grade\s*/i, '').trim();
  if (/^\d+$/.test(withoutGrade)) {
    return `Grade ${String(Number(withoutGrade))}`;
  }
  if (/^k(?:indergarten)?$/i.test(withoutGrade)) {
    return 'Kindergarten';
  }
  if (/^pre\s*k$/i.test(withoutGrade)) {
    return 'Pre-K';
  }

  if (/^grade\s+/i.test(normalized)) {
    return `Grade ${titleCaseWords(withoutGrade)}`;
  }

  return titleCaseWords(normalized);
};

const normalizeDivisionLabel = (value: unknown): string => {
  const normalized = normalizeDelimitedValue(value).toLowerCase();
  if (!normalized) return 'Division not specified';
  if (normalized.includes('pre')) return 'Pre-School';
  if (normalized === 'boys') return 'Boys';
  if (normalized === 'girls') return 'Girls';
  return titleCaseWords(normalized);
};

const mapDomainStatusToLabel = (value: unknown): string => {
  const normalized = readString(value).toLowerCase();
  if (!normalized) return 'Applied';
  if (normalized === 'draft' || normalized === 'submitted') return 'Applied';
  if (normalized === 'under_review' || normalized === 'interview_scheduled') return 'In-Progress';
  if (normalized === 'decision_pending') return 'Decision';
  if (normalized === 'accepted' || normalized === 'accepted_pending_enrollment') return 'Accepted';
  if (normalized === 'denied') return 'Denied';
  if (normalized === 'waitlisted') return 'Waitlisted';
  if (normalized === 'enrolled') return 'Enrolled';
  if (normalized === 'withdrawn' || normalized === 'acceptance_expired') return 'Archived';
  return titleCaseWords(normalized.replace(/_/g, ' '));
};

const formatDateLabel = (epochSeconds: unknown): string => {
  const unixSeconds = readUnixSeconds(epochSeconds);
  if (unixSeconds == null || unixSeconds <= 0) return 'Not specified';
  try {
    return new Date(unixSeconds * 1000).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'Not specified';
  }
};

const formatDateMonthDay = (epochSeconds: unknown): { month: string; day: string; fullDate: string; time: string } => {
  const unixSeconds = readUnixSeconds(epochSeconds);
  if (unixSeconds == null || unixSeconds <= 0) {
    return { month: '--', day: '--', fullDate: 'Date unavailable', time: '' };
  }

  try {
    const date = new Date(unixSeconds * 1000);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.toLocaleDateString('en-US', { day: '2-digit' }),
      fullDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    };
  } catch {
    return { month: '--', day: '--', fullDate: 'Date unavailable', time: '' };
  }
};

const joinNames = (...tokens: unknown[]): string => {
  const parts = tokens.map((token) => readString(token)).filter(Boolean);
  return parts.join(' ').trim();
};

const normalizeAcademicYear = (value: unknown): string => {
  const normalized = readString(value);
  if (!normalized) return 'Not specified';
  if (/^\d{4}-\d{4}$/.test(normalized)) {
    return `${normalized.slice(0, 4)}-${normalized.slice(7)}`;
  }
  return normalized;
};

const toChildProfiles = (
  application: Record<string, unknown> | undefined,
  detailStatus: string | undefined,
  assigneeName: string | undefined,
  staffNameById: Map<string, string>,
): ChildProfile[] => {
  if (!application) return [];

  const children = asRecordArray(application.children);
  const applicationStatus = mapDomainStatusToLabel(application.status);
  const resolvedStatus = readString(detailStatus) || applicationStatus;
  const appAcademicYear = normalizeAcademicYear(
    application.academicYear
      ?? application.schoolYear
      ?? application.applicationYear
      ?? application.currentAcademicYear,
  );
  const fallbackAssignee = sanitizeAssigneeLabel(assigneeName) || 'Unassigned';

  return children.map((child, index) => {
    const childInfo = asRecord(child.childInfo);
    const healthInfo = asRecord(child.healthInfo);

    const fullName = joinNames(childInfo.firstName, childInfo.lastName) || `Child ${index + 1}`;
    const hebrewName = joinNames(childInfo.hebrewFirstName, childInfo.hebrewMiddleName, childInfo.hebrewLastName);

    const childAssignee = sanitizeAssigneeLabel(asRecord(child.assignee).name)
      || sanitizeAssigneeLabel(child.assigneeName)
      || sanitizeAssigneeLabel(child.assignedToName)
      || sanitizeAssigneeLabel(staffNameById.get(readString(child.assignedTo)))
      || sanitizeAssigneeLabel(child.assignedTo)
      || fallbackAssignee;

    return {
      id: readString(child.id) || `child-${index + 1}`,
      name: fullName,
      preferredName: toDisplayOrFallback(childInfo.calledBy),
      hebrewName: toDisplayOrFallback(hebrewName),
      applicationYear: appAcademicYear,
      gradeApplyingFor: normalizeGradeLabel(child.gradeApplyingFor),
      dateOfBirth: formatDateLabel(childInfo.dateOfBirth),
      placeOfBirth: toDisplayOrFallback(childInfo.placeOfBirth),
      languageAtHome: toDisplayOrFallback(childInfo.languageAtHome),
      currentSchool: toDisplayOrFallback(childInfo.currentSchool),
      currentGrade: normalizeGradeLabel(childInfo.currentGrade),
      principalName: toDisplayOrFallback(childInfo.principalName),
      schoolPhone: toDisplayOrFallback(childInfo.principalPhone),
      pediatrician: toDisplayOrFallback(healthInfo.physicianName),
      pediatricianPhone: toDisplayOrFallback(healthInfo.physicianPhone),
      dentist: toDisplayOrFallback(healthInfo.dentistName),
      dentistPhone: toDisplayOrFallback(healthInfo.dentistPhone),
      conditions: buildAdmissionsChildMedicalSummary({
        specialNeeds: healthInfo.specialNeeds,
        allergies: healthInfo.allergies,
        medical: child.medical,
      }),
      division: normalizeDivisionLabel(child.schoolDivision),
      status: resolvedStatus,
      assigneeName: childAssignee,
      avatarUrl: readString(childInfo.avatarUrl),
    };
  });
};

const statusBadgeClassName = (status: string): string => {
  const normalized = status.toLowerCase();
  if (normalized.includes('enroll')) return 'bg-jungle/10 text-jungle';
  if (normalized.includes('accept')) return 'bg-primary/10 text-primary';
  if (normalized.includes('deny') || normalized.includes('archive')) return 'bg-strawberry/10 text-strawberry';
  return 'bg-primary/10 text-primary';
};

const SectionHeading = ({ icon: Icon, title }: { icon: React.ElementType; title: string }) => (
  <header className="col-span-1 md:col-span-2 mt-4 mb-2 border-b border-gray-100 pb-2 first:mt-0 flex items-center gap-2">
    <Icon size={14} className="text-primary" />
    <h4 className="text-xs font-bold text-irongrey/60 uppercase tracking-widest">{title}</h4>
  </header>
);

const DetailField = ({
  label,
  value,
  isEditing,
  onChange,
  multiline,
  options,
  className,
  dir,
  showHebrewKeyboardButton,
  isKeyboardOpen,
  onToggleKeyboard,
  onAppendHebrewKey,
  onCloseKeyboard,
}: {
  label: string;
  value: string;
  isEditing: boolean;
  onChange?: (nextValue: string) => void;
  multiline?: boolean;
  options?: string[];
  className?: string;
  dir?: 'ltr' | 'rtl';
  showHebrewKeyboardButton?: boolean;
  isKeyboardOpen?: boolean;
  onToggleKeyboard?: () => void;
  onAppendHebrewKey?: (nextValue: string) => void;
  onCloseKeyboard?: () => void;
}) => (
  <section className={className}>
    <dt className="text-[10px] uppercase font-bold text-irongrey/40 mb-1 tracking-wider">{label}</dt>
    <dd>
      {isEditing && onChange ? (
        options && options.length > 0 ? (
          <select
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary"
          >
            {options.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        ) : multiline ? (
          <textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            rows={3}
            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm font-medium text-irongrey focus:ring-primary focus:border-primary resize-none"
            dir={dir}
          />
        ) : (
          <section className="relative">
            <input
              value={value}
              onChange={(event) => onChange(event.target.value)}
              className={`w-full bg-gray-50 border border-gray-200 rounded-xl py-2 text-sm font-semibold text-irongrey focus:ring-primary focus:border-primary ${showHebrewKeyboardButton ? 'pl-3 pr-10' : 'px-3'}`}
              dir={dir}
            />
            {showHebrewKeyboardButton ? (
              <button
                type="button"
                aria-label="Hebrew keyboard"
                onClick={onToggleKeyboard}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-irongrey/30 hover:text-primary transition-colors"
              >
                אב
              </button>
            ) : null}
            {showHebrewKeyboardButton && isKeyboardOpen ? (
              <section className="absolute left-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 p-3 z-50 animate-in fade-in zoom-in-95 duration-150">
                <section className="grid grid-cols-8 gap-1.5">
                  {HEBREW_KEYS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => onAppendHebrewKey?.(key)}
                      className="h-8 w-8 flex items-center justify-center bg-gray-50 hover:bg-primary/10 hover:text-primary rounded-lg text-sm font-bold text-irongrey transition-colors"
                    >
                      {key}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => onAppendHebrewKey?.(' ')}
                    className="col-span-4 h-8 flex items-center justify-center bg-gray-50 hover:bg-primary/10 hover:text-primary rounded-lg text-[10px] font-bold text-irongrey transition-colors uppercase tracking-widest"
                  >
                    Space
                  </button>
                  <button
                    type="button"
                    onClick={onCloseKeyboard}
                    className="col-span-4 h-8 flex items-center justify-center bg-primary text-white rounded-lg text-[10px] font-bold uppercase tracking-widest"
                  >
                    Done
                  </button>
                </section>
              </section>
            ) : null}
          </section>
        )
      ) : (
        <p className="text-sm font-semibold text-irongrey" dir={dir}>
          {isPlaceholder(value) ? <span className="italic text-irongrey/30 font-normal">Not specified</span> : value}
        </p>
      )}
    </dd>
  </section>
);

export const ChildrenTab: React.FC = () => {
  const {
    applicationId,
    isEditing = false,
    editResetToken = 0,
    detailStatus,
    assigneeName,
    staffMembers = [],
  } = useOutletContext<DetailContext>();
  const applicationQuery = useAdmissionsApplication(applicationId);
  const documentsQuery = useAdmissionsDocuments(applicationId);
  const timelineQuery = useAdmissionsTimeline(applicationId);
  const [updateChildApplication] = useUpdateAdmissionsChildApplicationMutation();
  const application = applicationQuery.data as unknown as Record<string, unknown> | undefined;

  const staffNameById = useMemo(() => {
    const mapping = new Map<string, string>();
    for (const member of staffMembers) {
      const id = readString(member?.id);
      const name = sanitizeAssigneeLabel(readString(member?.name));
      if (id && name) {
        mapping.set(id, name);
      }
    }
    return mapping;
  }, [staffMembers]);

  const sourceProfiles = useMemo(
    () => toChildProfiles(application, detailStatus, assigneeName, staffNameById),
    [application, detailStatus, assigneeName, staffNameById],
  );

  const photoResourceByChildId = useMemo(() => {
    const mapping = new Map<string, string>();
    const documents = documentsQuery.data?.items ?? [];
    let fallbackResource = '';
    for (const item of documents) {
      const record = asRecord(item);
      const resourceId = readString(record.resourceId);
      if (!resourceId) continue;
      const category = readString(record.category).toLowerCase();
      const fileName = readString(record.fileName).toLowerCase();
      const looksLikePhoto = category === 'other'
        && /photo|avatar|headshot|image|jpeg|jpg|png|recent/i.test(fileName || resourceId);
      if (!looksLikePhoto) continue;
      const childIds = Array.isArray(record.childApplicationIds)
        ? (record.childApplicationIds as unknown[]).map((entry) => readString(entry)).filter(Boolean)
        : [];
      if (childIds.length === 0 && !fallbackResource) {
        fallbackResource = resourceId;
      }
      for (const childId of childIds) {
        if (!mapping.has(childId)) {
          mapping.set(childId, resourceId);
        }
      }
    }
    if (fallbackResource) {
      mapping.set('__default__', fallbackResource);
    }
    return mapping;
  }, [documentsQuery.data?.items]);

  const [resolvedStorageUrls, setResolvedStorageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    const candidates = new Set<string>();
    for (const profile of sourceProfiles) {
      if (isStoragePath(profile.avatarUrl)) {
        candidates.add(profile.avatarUrl);
      }
      const docPath = photoResourceByChildId.get(profile.id) || photoResourceByChildId.get('__default__');
      if (docPath && isStoragePath(docPath)) {
        candidates.add(docPath);
      }
    }
    if (!candidates.size) return;
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const key of candidates) {
        try {
          const response = await getUrl({ path: key });
          next[key] = response.url.toString();
        } catch {
          // Ignore resolution failures; caller falls back to initials.
        }
      }
      if (!cancelled) {
        setResolvedStorageUrls((previous) => ({ ...previous, ...next }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [photoResourceByChildId, sourceProfiles]);

  const [draftById, setDraftById] = useState<Record<string, ChildProfile>>({});
  const [activeHebrewKeyboardFor, setActiveHebrewKeyboardFor] = useState<string | null>(null);
  const [activeAssigneeMenuFor, setActiveAssigneeMenuFor] = useState<string | null>(null);
  const assigneeMenuRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!activeAssigneeMenuFor) return undefined;
    const frame = requestAnimationFrame(() => {
      const first = assigneeMenuRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
      first?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [activeAssigneeMenuFor]);

  useEffect(() => {
    const nextDrafts: Record<string, ChildProfile> = {};
    for (const profile of sourceProfiles) {
      nextDrafts[profile.id] = {
        ...profile,
        ...(draftById[profile.id] ?? {}),
      };
    }
    setDraftById(nextDrafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceProfiles]);

  useEffect(() => {
    const nextDrafts: Record<string, ChildProfile> = {};
    for (const profile of sourceProfiles) {
      nextDrafts[profile.id] = { ...profile };
    }
    setDraftById(nextDrafts);
    setActiveHebrewKeyboardFor(null);
    // The reset token is parent-owned; sourceProfiles is sampled at the cancel moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editResetToken]);

  const timelineCards = useMemo(() => {
    const items = timelineQuery.data?.items ?? [];
    return items
      .map((entry) => asRecord(entry))
      .map((entry, index) => {
        const occurredAt = readUnixSeconds(entry.occurredAt);
        const date = formatDateMonthDay(occurredAt);
        const eventTitle = readString(entry.eventType) || readString(entry.summary) || readString(entry.type) || `Event ${index + 1}`;
        return {
          id: readString(entry.id) || `timeline-${index}`,
          title: formatEventLabel(eventTitle),
          dateLabel: date.fullDate,
          dayLabel: date.day,
          timeLabel: date.time,
          monthLabel: date.month,
        };
      })
      .sort((left, right) => right.dateLabel.localeCompare(left.dateLabel))
      .slice(0, 4)
      .map((entry) => ({
        id: entry.id,
        title: entry.title,
        dateLabel: entry.dateLabel,
        dayLabel: entry.dayLabel,
        timeLabel: entry.timeLabel,
        monthLabel: entry.monthLabel,
      }));
  }, [timelineQuery.data?.items]);

  const profiles = sourceProfiles.map((profile) => draftById[profile.id] ?? profile);
  const assigneeOptions = useMemo<StaffOption[]>(() => {
    const byName = new Map<string, StaffOption>();
    for (const member of staffMembers) {
      const id = readString(member?.id);
      const name = sanitizeAssigneeLabel(readString(member?.name));
      if (!name) continue;
      const normalizedName = name.toLowerCase();
      const persistId = UUID_LIKE_PATTERN.test(id) ? id : null;
      const existing = byName.get(normalizedName);
      if (!existing) {
        byName.set(normalizedName, { id, name, persistId });
        continue;
      }
      if (!existing.persistId && persistId) {
        byName.set(normalizedName, { id, name, persistId });
      }
    }
    const options: StaffOption[] = [
      { id: '', name: 'Unassigned', persistId: null },
      ...Array.from(byName.values()),
    ];
    return options.sort((left, right) => {
      if (!left.id) return -1;
      if (!right.id) return 1;
      return left.name.localeCompare(right.name);
    });
  }, [staffMembers]);

  const setDraftValue = <K extends keyof ChildProfile>(childId: string, key: K, value: ChildProfile[K]) => {
    setDraftById((previousDrafts) => {
      const base = previousDrafts[childId] ?? sourceProfiles.find((profile) => profile.id === childId);
      if (!base) return previousDrafts;
      return {
        ...previousDrafts,
        [childId]: {
          ...base,
          [key]: value,
        },
      };
    });
  };

  const resolveAvatarSource = (childId: string, avatarValue: string): string => {
    const direct = readString(avatarValue);
    if (direct) {
      if (!isStoragePath(direct)) return direct;
      return resolvedStorageUrls[direct] || '';
    }
    const documentPath = photoResourceByChildId.get(childId) || photoResourceByChildId.get('__default__') || '';
    if (!documentPath) return '';
    return isStoragePath(documentPath) ? (resolvedStorageUrls[documentPath] || '') : documentPath;
  };

  const applyChildAssignee = async (childId: string, selectedOption: StaffOption) => {
    const nextLabel = selectedOption.name;
    const previousLabel = (draftById[childId] ?? sourceProfiles.find((profile) => profile.id === childId))?.assigneeName ?? 'Unassigned';
    setDraftValue(childId, 'assigneeName', nextLabel);
    try {
      const isUnassigned = !selectedOption.id;
      await updateChildApplication({
        applicationId,
        childApplicationId: childId,
        assignedTo: selectedOption.persistId ?? null,
        assigneeName: isUnassigned ? undefined : selectedOption.name,
      }).unwrap();
    } catch {
      setDraftValue(childId, 'assigneeName', previousLabel);
    } finally {
      setActiveAssigneeMenuFor(null);
    }
  };

  useEffect(() => {
    if (!isEditing) {
      setActiveHebrewKeyboardFor(null);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!activeAssigneeMenuFor) {
      return () => {};
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (target.closest('[data-testid="admissions-assignee-menu"]') || target.closest('[data-testid="admissions-assignee-trigger"]')) {
        return;
      }
      setActiveAssigneeMenuFor(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [activeAssigneeMenuFor]);

  return (
    <section className="pb-8" data-testid="admissions-detail-children-tab">
      {applicationQuery.isLoading ? <p className="text-sm text-irongrey/50">Loading child details...</p> : null}
      {applicationQuery.error ? (
        <SupportErrorMessage>Unable to load child details right now.</SupportErrorMessage>
      ) : null}

      {profiles.length === 0 && !applicationQuery.isLoading && !applicationQuery.error ? (
        <article className="bg-white rounded-2xl p-7 shadow-card border border-gray-100">
          <p className="text-sm text-irongrey/60 italic">Child records are syncing.</p>
        </article>
      ) : null}

      <section className="grid grid-cols-1 gap-4">
        {profiles.map((child) => (
          <article
            key={child.id}
            data-testid="admissions-child-profile-card"
            className="bg-white rounded-3xl p-6 md:p-8 shadow-card border border-gray-100"
          >
            <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <section className="flex items-center gap-5 min-w-0">
                <figure className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-primary/10 flex items-center justify-center text-primary overflow-hidden shrink-0">
                  {resolveAvatarSource(child.id, child.avatarUrl) ? (
                    <img
                      src={resolveAvatarSource(child.id, child.avatarUrl)}
                      alt={`${child.name} avatar`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-bold">{child.name.slice(0, 2).toUpperCase()}</span>
                  )}
                </figure>
                <section className="min-w-0">
                  {isEditing ? (
                    <input
                      value={child.name}
                      onChange={(event) => setDraftValue(child.id, 'name', event.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xl md:text-2xl font-bold text-irongrey focus:ring-primary focus:border-primary"
                    />
                  ) : (
                    <h3 className="text-xl md:text-2xl font-bold text-irongrey truncate">{child.name}</h3>
                  )}
                  <p className="mt-1 flex flex-wrap items-center gap-2">
                    <span className={`px-3 py-1 text-[10px] md:text-xs font-bold rounded-full uppercase tracking-wider ${statusBadgeClassName(child.status)}`}>
                      {child.status}
                    </span>
                    <span className="text-[11px] md:text-sm text-irongrey/40 font-medium">ID: {child.id}</span>
                  </p>
                </section>
              </section>

              <section className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto border-t sm:border-t-0 pt-4 sm:pt-0 border-gray-100">
                <p className="text-[11px] md:text-sm font-bold text-irongrey/30 uppercase tracking-widest">{child.division}</p>
                <section className="relative">
                  <button
                    type="button"
                    data-testid="admissions-assignee-trigger"
                    onClick={() => setActiveAssigneeMenuFor((current) => (current === child.id ? null : child.id))}
                    className="flex items-center gap-2 bg-primary/5 text-primary px-3 md:px-4 py-2 rounded-lg text-[80%] md:text-[85%] font-medium max-w-[150px] md:max-w-none"
                  >
                    <UserRoundCheck size={16} className="shrink-0" />
                    <span className="truncate">{child.assigneeName}</span>
                    <ChevronDown
                      size={14}
                      className={`shrink-0 transition-transform ${activeAssigneeMenuFor === child.id ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {activeAssigneeMenuFor === child.id ? (
                    <section
                      ref={assigneeMenuRef}
                      data-testid="admissions-assignee-menu"
                      role="menu"
                      aria-label={`Select staff for ${child.name}`}
                      className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-soft border border-gray-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-150"
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          setActiveAssigneeMenuFor(null);
                          return;
                        }
                        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                          return;
                        }
                        const container = assigneeMenuRef.current;
                        if (!container) return;
                        const menuItems = Array.from(
                          container.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
                        );
                        if (menuItems.length === 0) return;
                        event.preventDefault();
                        const activeIndex = menuItems.findIndex(
                          (item) => item === document.activeElement,
                        );
                        const nextIndex =
                          event.key === 'Home'
                            ? 0
                            : event.key === 'End'
                              ? menuItems.length - 1
                              : event.key === 'ArrowDown'
                                ? (activeIndex + 1) % menuItems.length
                                : activeIndex <= 0
                                  ? menuItems.length - 1
                                  : activeIndex - 1;
                        menuItems[nextIndex]?.focus();
                      }}
                    >
                      <section className="px-4 py-1.5 border-b border-gray-50 mb-1">
                        <span className="text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Select Staff</span>
                      </section>
                      <section className="max-h-60 overflow-y-auto">
                        {assigneeOptions.map((option) => (
                          <button
                            key={`${option.id || 'unassigned'}:${option.name}`}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              void applyChildAssignee(child.id, option);
                            }}
                            className={`w-full text-left px-4 py-2 text-xs font-semibold hover:bg-gray-50 transition-colors ${
                              child.assigneeName === option.name ? 'text-primary font-bold bg-primary/5' : 'text-irongrey'
                            }`}
                          >
                            {option.name}
                          </button>
                        ))}
                      </section>
                    </section>
                  ) : null}
                </section>
              </section>
            </header>

            <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
              <SectionHeading icon={FileText} title="This Application" />
              <DetailField
                label="Applying For"
                value={child.applicationYear}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'applicationYear', nextValue)}
                options={['2025-26', '2026-27', '2027-28']}
              />
              <DetailField
                label="Grade Applying For"
                value={child.gradeApplyingFor}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'gradeApplyingFor', nextValue)}
              />

              <SectionHeading icon={User} title="Birth & Identity" />
              <DetailField
                label="Name Child Is Called By"
                value={child.preferredName}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'preferredName', nextValue)}
              />
              <DetailField
                label="Hebrew Name"
                value={child.hebrewName}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'hebrewName', nextValue)}
                dir="rtl"
                showHebrewKeyboardButton
                isKeyboardOpen={activeHebrewKeyboardFor === child.id}
                onToggleKeyboard={() => setActiveHebrewKeyboardFor((current) => (current === child.id ? null : child.id))}
                onCloseKeyboard={() => setActiveHebrewKeyboardFor(null)}
                onAppendHebrewKey={(key) => setDraftValue(child.id, 'hebrewName', `${child.hebrewName}${key}`)}
              />
              <DetailField
                label="Date of Birth"
                value={child.dateOfBirth}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'dateOfBirth', nextValue)}
              />
              <DetailField
                label="Place of Birth"
                value={child.placeOfBirth}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'placeOfBirth', nextValue)}
              />
              <DetailField
                label="Language at Home"
                value={child.languageAtHome}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'languageAtHome', nextValue)}
              />

              <SectionHeading icon={School} title="Current Schooling" />
              <DetailField
                label="Current School"
                value={child.currentSchool}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'currentSchool', nextValue)}
              />
              <DetailField
                label="Current Grade"
                value={child.currentGrade}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'currentGrade', nextValue)}
              />
              <DetailField
                label="Director/Principal"
                value={child.principalName}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'principalName', nextValue)}
              />
              <DetailField
                label="Phone Number"
                value={child.schoolPhone}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'schoolPhone', nextValue)}
              />

              <SectionHeading icon={Stethoscope} title="Medical Contacts" />
              <DetailField
                label="Pediatrician"
                value={child.pediatrician}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'pediatrician', nextValue)}
              />
              <DetailField
                label="Phone"
                value={child.pediatricianPhone}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'pediatricianPhone', nextValue)}
              />
              <DetailField
                label="Dentist"
                value={child.dentist}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'dentist', nextValue)}
              />
              <DetailField
                label="Phone"
                value={child.dentistPhone}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'dentistPhone', nextValue)}
              />

              <SectionHeading icon={Stethoscope} title="Medical Conditions" />
              <DetailField
                label="Conditions & Allergies"
                value={child.conditions}
                isEditing={isEditing}
                onChange={(nextValue) => setDraftValue(child.id, 'conditions', nextValue)}
                multiline
                className="col-span-1 md:col-span-2"
              />

              <section className="col-span-1 md:col-span-2 mt-2 border-t border-gray-50 pt-4">
                <header className="mb-3 flex items-center gap-2">
                  <CalendarDays size={12} className="text-irongrey/40" />
                  <h5 className="text-[10px] uppercase font-bold text-irongrey/40 tracking-wider">Scheduling</h5>
                </header>
                <section className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100">
                  {timelineQuery.isLoading ? (
                    <p className="text-xs text-irongrey/50">Loading schedule...</p>
                  ) : null}
                  {!timelineQuery.isLoading && timelineCards.length === 0 ? (
                    <p className="text-xs text-irongrey/40 italic">No upcoming events scheduled for this child.</p>
                  ) : null}
                  {timelineCards.length > 0 ? (
                    <ul className="space-y-3" data-testid="admissions-child-schedule-list">
                      {timelineCards.map((event) => (
                        <li key={event.id} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-center gap-4">
                          <section className="bg-primary/5 p-2 rounded-lg text-center min-w-[52px] border border-primary/10">
                            <p className="text-[8px] uppercase font-bold text-primary">{event.monthLabel}</p>
                            <p className="text-sm font-black text-irongrey">{event.dayLabel}</p>
                          </section>
                          <section className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-irongrey truncate">{event.title}</p>
                            <p className="text-[10px] text-irongrey/50 font-medium">
                              {event.dateLabel}
                              {event.timeLabel ? ` • ${event.timeLabel}` : ''}
                            </p>
                          </section>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              </section>
            </section>
          </article>
        ))}
      </section>
    </section>
  );
};
